import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Orb from "../components/Orb/Orb";
import OrbBlue from "../components/Orb/OrbBlue";
import { motion } from "motion/react";
import Counter from "../components/Counter/Counter";

interface IntroProps {
  onComplete?: () => void;
}

const Intro: React.FC<IntroProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [totalMilliseconds, setTotalMilliseconds] = useState(0);
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute("data-theme");
      setIsDarkTheme(theme === "dark");
    };

    // Check initial theme
    checkTheme();

    // Observe theme changes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          checkTheme();
          break;
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const eventDate = new Date("2026-01-23T09:00:00").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = eventDate - now;

      if (difference > 0) {
        setTotalMilliseconds(difference);
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 10);
    return () => clearInterval(timer);
  }, []);

  const getTimeValues = (milliseconds: number) => {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    const millis = Math.floor((milliseconds % 1000) / 10);

    return { days, hours, minutes, seconds, millis };
  };

  const timeValues = getTimeValues(totalMilliseconds);
  const timeBlocks = [
    { label: "DAYS", value: timeValues.days, maxDigits: 2 },
    { label: "HOURS", value: timeValues.hours, maxDigits: 2 },
    { label: "MINUTES", value: timeValues.minutes, maxDigits: 2 },
    { label: "SECONDS", value: timeValues.seconds, maxDigits: 2 },
    { label: "MS", value: timeValues.millis, maxDigits: 2 },
  ];

  const getPlaces = (maxDigits: number) => {
    const places = [];
    for (let i = Math.pow(10, maxDigits - 1); i >= 1; i /= 10) {
      if (i >= 1) places.push(i);
    }
    return places;
  };

  const handleExploreHackathon = () => {
    navigate("/hackathon");
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Conditionally render Orb based on theme */}
      <div className="fixed inset-0 z-0">
        {isDarkTheme ? (
          <Orb
            hue={0}
            hoverIntensity={0.5}
            rotateOnHover={true}
            forceHoverState={false}
          />
        ) : (
          <OrbBlue
            hue={210}
            hoverIntensity={0.5}
            rotateOnHover={true}
            forceHoverState={false}
          />
        )}
      </div>

      {/* Conditional gradient overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{
          background: isDarkTheme
            ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1), transparent, rgba(249, 115, 22, 0.05))"
            : "linear-gradient(135deg, rgba(59, 130, 246, 0.1), transparent, rgba(139, 92, 246, 0.05))",
        }}
      />

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-20 sm:py-12">
        <div className="max-w-6xl w-full">
          <br />
          {/* Title */}
          <div
            className={`text-center mb-6 sm:mb-4 transition-all duration-1000 ${
              showContent
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-4 scale-95"
            }`}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-2 px-4">
              <span
                className="text-transparent"
                style={{
                  background:
                    "linear-gradient(to right, var(--color-primary-dark), var(--color-primary), var(--color-accent))",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  animation: "gradient 3s ease infinite",
                }}
              >
                ASTRANOVA
              </span>
            </h1>
          </div>

          {/* Tagline */}
          <div
            className={`text-center mb-8 sm:mb-8 transition-all duration-1000 ${
              showContent ? "opacity-100" : "opacity-0"
            }`}
          >
            <p
              className="text-xl sm:text-2xl md:text-3xl font-light tracking-wide px-4"
              style={{ color: "var(--color-text)" }}
            >
              Ignite the Future
            </p>
          </div>

          {/* Date */}
          <div
            className={`text-center mb-10 sm:mb-12 transition-all duration-1000 ${
              showContent ? "opacity-100" : "opacity-0"
            }`}
          >
            <div
              className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-8 py-2.5 sm:py-3 backdrop-blur-md rounded-full shadow-xl mx-4"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                border: "2px solid var(--color-border)",
              }}
            >
              <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
                <span
                  className="animate-ping absolute h-full w-full rounded-full"
                  style={{
                    backgroundColor: "var(--color-accent)",
                    opacity: 0.75,
                  }}
                />
                <span
                  className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, var(--color-primary-dark), var(--color-primary), var(--color-accent))",
                  }}
                />
              </span>

              <span
                className="font-semibold text-sm sm:text-base"
                style={{ color: "var(--color-text)" }}
              >
                January 23 & 24, 2026
              </span>
            </div>
          </div>

          <div
            className={`text-center mb-6 sm:mb-8 transition-all duration-1000 ${
              showContent ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="relative inline-block">
              <h2
                className="text-lg sm:text-xl md:text-2xl font-bold tracking-widest uppercase px-6 py-3"
                style={{
                  color: "var(--color-primary)",
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid var(--color-primary)",
                  borderRadius: "8px",
                  opacity: "0.9",
                }}
              >
                TIME TO RESOLUTION
              </h2>
            </div>
          </div>

          {/* Countdown */}
          <div
            className={`text-center transition-all duration-1000 px-4 ${
              showContent ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 max-w-4xl mx-auto">
              {timeBlocks.map((block, index) => (
                <motion.div
                  key={block.label}
                  className="flex flex-col items-center justify-center"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <div
                    className="mb-1 sm:mb-2"
                    style={{
                      height: "65px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Counter
                      value={block.value}
                      fontSize={block.label === "MS" ? 32 : 40}
                      padding={4}
                      places={getPlaces(block.maxDigits)}
                      gap={2}
                      borderRadius={4}
                      horizontalPadding={6}
                      textColor="var(--color-primary)"
                      fontWeight="900"
                      containerStyle={{
                        backgroundColor: "transparent",
                        padding: "0",
                        border: "none",
                        borderRadius: "0",
                      }}
                      counterStyle={{
                        color: "var(--color-primary)",
                      }}
                      gradientHeight={0}
                      digitStyle={{
                        minWidth: block.maxDigits === 2 ? "2ch" : "1ch",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    />
                  </div>

                  <div
                    className="text-xs sm:text-sm uppercase tracking-widest font-bold mt-1"
                    style={{
                      color: "var(--color-primary)",
                      letterSpacing: "0.15em",
                    }}
                  >
                    {block.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Flagship Events Section */}
          <div
            className={`mt-16 sm:mt-20 transition-all duration-1000 delay-500 ${
              showContent
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              {/* Divider */}
              <div className="flex items-center justify-center mb-12">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-red-500/40 to-transparent"></div>
                <div className="mx-6 px-6 py-3 rounded-full bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/40">
                  <span className="text-white-400 font-bold tracking-widest text-lg">
                    FLAGSHIP EVENTS
                  </span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-red-500/40 to-transparent"></div>
              </div>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    title: "Paper Presentation",
                    desc: "Showcase innovative ideas and research to a panel of experts.",
                  },
                  {
                    title: "IPL Auction",
                    desc: "Bid smart, build your dream team, and test your strategy skills.",
                  },
                  {
                    title: "Seminar",
                    desc: "Entrepreneurship Oppurtunities in Emerging Technologies",
                  },
                  {
                    title: "Workshop",
                    desc: "Git & GitHub Essentials for efficient code management and collaboration, along with Figma for Developers",
                  },
                  {
                    title: "Rapid Coding",
                    desc: "Fast-paced coding challenges to test logic and speed.",
                  },
                  {
                    title: "Treasure Hunt",
                    desc: "Solve clues, crack puzzles, and race against time.",
                  },
                ].map((event, index) => (
                  <motion.div
                    key={event.title}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.03 }}
                    className="relative bg-gradient-to-br from-red-900/20 to-black/60 backdrop-blur-md border border-red-800/30 rounded-2xl p-6 sm:p-7 shadow-xl hover:border-red-600/50 transition-all duration-300"
                  >
                    <h3 className="text-xl sm:text-2xl font-bold mb-3">
                      <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                        {event.title}
                      </span>
                    </h3>

                    <p className="text-gray-300 text-sm sm:text-base mb-6 leading-relaxed">
                      {event.desc}
                    </p>

                    <motion.button
                      className="w-full px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
navigate("/passes")
                      }}
                    >
                      Explore Details →
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <style>{`
            @keyframes gradient {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default Intro;
